import { Activity, Dumbbell } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Fade, Lines, Reveal } from "@/components/motion";
import CutoutImage from "@/components/CutoutImage";
import { BookingButton, WhatsAppAssist, WhatsAppButton } from "@/components/CTA";

const CONSIDERS = [
  "Obiettivi personali",
  "Livello iniziale",
  "Esperienza di allenamento",
  "Tempo disponibile",
  "Attrezzatura disponibile",
  "Caratteristiche individuali",
  "Progressione nel tempo",
  "Monitoraggio e adattamento",
];

const CONTEXTS = [
  "Corpo libero",
  "Allenamento a casa",
  "Allenamento in palestra",
  "Allenamento in acqua e programmazione del nuoto",
  "Forza",
  "Ipertrofia",
  "Tonificazione",
  "Composizione corporea",
  "Ritenzione idrica",
  "Performance",
  "Salute e benessere",
];

export default function Allenamento() {
  useSEO(SEO.allenamento);
  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-20 sm:pb-28">
        <div className="grid items-start gap-14 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <Fade>
              <p className="eyebrow text-wave-deep">Allenamento · Bologna e Casalecchio di Reno</p>
            </Fade>
            <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <Lines delay={0.2} lines={["Allenamento", <em key="e" className="text-wave-deep">personalizzato</em>]} />
            </h1>
            <Fade delay={0.6}>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/65">
                La programmazione non nasce da schede standard. Il punto di partenza conta:
                obiettivi, livello, esperienza, tempo disponibile, attrezzatura e contesto di vita.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/60">
                Da qui costruisco una progressione ragionata, monitorata e adattata nel tempo — che
                l'obiettivo sia la salute, la composizione corporea, la performance o il benessere
                generale.
              </p>
            </Fade>
          </div>
          <Fade delay={0.4} className="relative">
            <figure>
              <CutoutImage
                src={SITE.images.training}
                alt="Programmazione dell'allenamento personalizzata a Bologna e Casalecchio di Reno"
                width={1100}
                height={1173}
                testId="training-photo"
                className="mx-auto max-w-xs sm:max-w-sm"
              />
              <figcaption className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-ink/45">
                Lo studio del movimento, praticato in prima persona
              </figcaption>
            </figure>
          </Fade>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="Come viene costruito il programma">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 py-20 sm:py-28 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              La programmazione considera
            </h2>
            <ul className="mt-9 flex flex-col">
              {CONSIDERS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 border-t border-ink/10 py-4 text-sm sm:text-base text-ink/75 last:border-b"
                >
                  <Activity aria-hidden="true" className="h-4 w-4 shrink-0 text-wave-deep" strokeWidth={1.75} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Contesti e obiettivi
            </h2>
            <ul className="mt-9 flex flex-col">
              {CONTEXTS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-4 border-t border-ink/10 py-4 text-sm sm:text-base text-ink/75 last:border-b"
                >
                  <Dumbbell aria-hidden="true" className="h-4 w-4 shrink-0 text-wave-deep" strokeWidth={1.75} />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-ink/10" aria-label="Richiedi informazioni sull'allenamento">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 py-20 sm:py-28 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Vuoi un programma di allenamento costruito su di te?
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">
              Il primo passo è una prima visita: valuteremo obiettivi e punto di partenza per
              impostare il tuo percorso di allenamento personalizzato.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <BookingButton label="Scopri il percorso di programmazione dell'allenamento" testId="training-booking-cta" />
              <WhatsAppButton label="Richiedi informazioni" testId="training-whatsapp-cta" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <WhatsAppAssist testId="training-whatsapp-assist" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
